import { fetchAuthSession } from 'aws-amplify/auth';
import { Signer } from '@aws-amplify/core/internals/utils';
import { NORMALIZED_STAGE } from 'src/constants/EnvironmentConstants';

const WORKDOCS_API_BASE_BY_STAGE: Record<string, string> = {
  alpha: 'https://q6o1buapo6.execute-api.us-west-2.amazonaws.com/alpha',
  beta: 'https://shzhy26cmg.execute-api.us-west-2.amazonaws.com/beta',
  prod: 'https://5smeqjxb33.execute-api.us-west-2.amazonaws.com/prod'
};

const WORKDOCS_APP_ID_BY_STAGE: Record<string, string> = {
  alpha: '31026e57-2f3e-415d-8494-afe5083c9ad7',
  beta: '766cf54b-d911-4524-9b79-895196547595',
  prod: '7dea905e-cb8b-441c-b43d-61d8b23bb266'
};

const WORKDOCS_API_BASE = WORKDOCS_API_BASE_BY_STAGE[NORMALIZED_STAGE] || WORKDOCS_API_BASE_BY_STAGE.alpha;

// Root WorkDocs folder: "Fintech Leadership Strategy Review" (contains year subfolders)
export const TECH_MBR_FOLDER_ID = '53a0043f87175d80d54e69318e664d9edca1f4907658675f76b377881de80492';

export const WORKDOCS_APP_ID = WORKDOCS_APP_ID_BY_STAGE[NORMALIZED_STAGE] || WORKDOCS_APP_ID_BY_STAGE.alpha;

export interface WorkDocsDocument {
  id: string;
  name: string;
  contentType: string;
  size: number;
  createdTimestamp: string;
  modifiedTimestamp: string;
  creatorId: string;
  latestVersionId: string;
}

export interface WorkDocsFolder {
  id: string;
  name: string;
  size: number;
  createdTimestamp: string;
  modifiedTimestamp: string;
  creatorId: string;
}

export interface FolderContentsResponse {
  documents: WorkDocsDocument[];
  folders: WorkDocsFolder[];
}

export interface CreateFolderResponse {
  folderId: string;
  name: string;
  parentFolderId: string;
}

export interface UploadDocumentResponse {
  documentId: string;
  versionId: string;
  name: string;
  parentFolderId: string;
}

export interface DeleteDocumentResponse {
  documentId: string;
  deleted: boolean;
}

interface ZocaloVersionMetadata {
  Name?: string;
  ContentType?: string;
  Size?: number;
  Id?: string;
}

interface ZocaloDocumentResponse {
  Id: string;
  LatestVersionMetadata?: ZocaloVersionMetadata;
  Created?: string;
  Modified?: string;
  CreatorId?: string;
}

interface ZocaloFolderResponse {
  Id: string;
  Name?: string;
  Size?: number;
  LatestVersionMetadata?: ZocaloVersionMetadata;
  Created?: string;
  Modified?: string;
  CreatorId?: string;
}

interface ZocaloFolderContentsResponse {
  Documents?: ZocaloDocumentResponse[];
  documents?: ZocaloDocumentResponse[];
  Folders?: ZocaloFolderResponse[];
  folders?: ZocaloFolderResponse[];
  Marker?: string;
}

export interface DownloadUrlResponse {
  documentId: string;
  versionId: string;
  name: string;
  contentType: string;
  size: number;
  downloadUrl: string;
  workdocsUrl: string;
}

export interface TokenResponse {
  // `accessToken` is itself a base64-encoded JSON descriptor that carries the
  // absolute `expiryTimeInMS` (and the nested WorkDocs JWT). Use getTokenExpiry
  // to read the expiry — it is NOT a top-level field on this response.
  accessToken: string;
  refreshToken: string;
  user?: { Id: string; RootFolderId: string };
}

/**
 * Extracts the absolute epoch-ms expiry from a WorkDocs access token. The token
 * the backend returns is a base64-encoded JSON descriptor whose `expiryTimeInMS`
 * is the single source of truth for expiry. Returns null if it can't be read.
 */
export function getTokenExpiry(accessToken: string): number | null {
  try {
    const decoded = JSON.parse(atob(accessToken)) as { expiryTimeInMS?: number };
    return typeof decoded.expiryTimeInMS === 'number' && decoded.expiryTimeInMS > 0 ? decoded.expiryTimeInMS : null;
  } catch {
    return null;
  }
}

async function signedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) throw new Error('No AWS credentials available');

  const request = {
    method: options.method || 'GET',
    url,
    headers: { 'Content-Type': 'application/json' },
    data: options.body as string | undefined
  };

  const signed = Signer.sign(
    request,
    {
      access_key: credentials.accessKeyId,
      secret_key: credentials.secretAccessKey,
      session_token: credentials.sessionToken || ''
    },
    { region: 'us-west-2', service: 'execute-api' }
  );

  return fetch(url, { ...options, headers: { ...signed.headers, ...((options.headers as Record<string, string>) || {}) } });
}

async function signedFetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const resp = await signedFetch(url, options);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).detail || (err as Record<string, string>).error || `Request failed: ${resp.status}`);
  }
  return resp.json();
}

export async function exchangeAuthCode(authCode: string, codeVerifier: string): Promise<TokenResponse> {
  return signedFetchJson<TokenResponse>(`${WORKDOCS_API_BASE}/token`, {
    method: 'POST',
    body: JSON.stringify({ authCode, codeVerifier })
  });
}

export async function refreshAccessToken(accessToken: string, refreshToken: string): Promise<TokenResponse> {
  return signedFetchJson<TokenResponse>(`${WORKDOCS_API_BASE}/token/refresh`, {
    method: 'POST',
    body: JSON.stringify({ accessToken, refreshToken })
  });
}

export async function listFolderContents(folderId: string, accessToken: string): Promise<FolderContentsResponse> {
  const allDocuments: WorkDocsDocument[] = [];
  const allFolders: WorkDocsFolder[] = [];
  let marker: string | undefined;

  do {
    const url = `${WORKDOCS_API_BASE}/folders/${folderId}/contents?token=${encodeURIComponent(accessToken)}${
      marker ? `&marker=${encodeURIComponent(marker)}` : ''
    }`;
    const data = await signedFetchJson<ZocaloFolderContentsResponse>(url, { method: 'GET' });

    allDocuments.push(
      ...(data.Documents || data.documents || []).map((doc: ZocaloDocumentResponse) => ({
        id: doc.Id,
        name: doc.LatestVersionMetadata?.Name || 'Unknown',
        contentType: doc.LatestVersionMetadata?.ContentType || '',
        size: doc.LatestVersionMetadata?.Size || 0,
        createdTimestamp: doc.Created || '',
        modifiedTimestamp: doc.Modified || '',
        creatorId: doc.CreatorId || '',
        latestVersionId: doc.LatestVersionMetadata?.Id || ''
      }))
    );

    allFolders.push(
      ...(data.Folders || data.folders || []).map((f: ZocaloFolderResponse) => ({
        id: f.Id,
        name: f.Name || 'Unknown',
        size: f.Size || f.LatestVersionMetadata?.Size || 0,
        createdTimestamp: f.Created || '',
        modifiedTimestamp: f.Modified || '',
        creatorId: f.CreatorId || ''
      }))
    );

    marker = data.Marker || undefined;
  } while (marker);

  return {
    documents: allDocuments,
    folders: allFolders
  };
}

export async function getDocumentDownloadUrl(documentId: string, accessToken: string): Promise<DownloadUrlResponse> {
  return signedFetchJson<DownloadUrlResponse>(`${WORKDOCS_API_BASE}/documents/${documentId}/download-url?token=${encodeURIComponent(accessToken)}`, {
    method: 'GET'
  });
}

/**
 * Creates a single sub-folder named `name` under `parentFolderId`. The backend does
 * NOT de-duplicate by name — calling this twice makes two same-named folders — so
 * prefer {@link findOrCreateFoldersUnder} unless the parent is known to be empty.
 */
export async function createFolder(parentFolderId: string, name: string, accessToken: string): Promise<CreateFolderResponse> {
  return signedFetchJson<CreateFolderResponse>(`${WORKDOCS_API_BASE}/create-folder?token=${encodeURIComponent(accessToken)}`, {
    method: 'POST',
    body: JSON.stringify({ parentFolderId, name })
  });
}

/**
 * Uploads a document into a WorkDocs folder. `base64Content` is the raw file bytes
 * base64-encoded; the backend performs the Zocalo three-step upload (initiate ->
 * PUT to a presigned S3 URL -> mark the version ACTIVE) on the caller's behalf.
 *
 * The backend does NOT de-duplicate by name — uploading the same file name twice
 * creates two documents — so callers that want replace-semantics must delete first.
 */
export async function uploadDocument(
  parentFolderId: string,
  name: string,
  contentType: string,
  base64Content: string,
  accessToken: string
): Promise<UploadDocumentResponse> {
  return signedFetchJson<UploadDocumentResponse>(`${WORKDOCS_API_BASE}/documents?token=${encodeURIComponent(accessToken)}`, {
    method: 'POST',
    body: JSON.stringify({ parentFolderId, name, contentType, content: base64Content })
  });
}

/**
 * Deletes a document from WorkDocs by id. The backend proxies the Zocalo delete on the
 * caller's behalf, so a caller can only remove documents their access token is permitted to
 * delete. The backend normalizes WorkDocs' 204 No Content to `{ documentId, deleted: true }`.
 *
 * Idempotency is the backend's responsibility — a second delete of an already-removed id may
 * surface an error; callers refresh the folder listing on success either way.
 */
export async function deleteDocument(documentId: string, accessToken: string): Promise<DeleteDocumentResponse> {
  return signedFetchJson<DeleteDocumentResponse>(`${WORKDOCS_API_BASE}/documents/${documentId}?token=${encodeURIComponent(accessToken)}`, {
    method: 'DELETE'
  });
}

/**
 * Idempotently ensures every name in `names` exists as a folder under
 * `parentFolderId`, returning a `name -> folderId` map. Lists the parent's
 * children ONCE, reuses any folder whose name matches exactly, and creates only
 * the missing ones (sequentially). This is the single idempotency choke point for
 * folder provisioning: a first run, a resume after a partial failure, and a
 * re-provision after an accidental deletion all flow through here and converge on
 * the same tree.
 */
export async function findOrCreateFoldersUnder(parentFolderId: string, names: string[], accessToken: string): Promise<Map<string, string>> {
  const { folders } = await listFolderContents(parentFolderId, accessToken);
  const existingByName = new Map(folders.map((folder) => [folder.name, folder.id]));

  const result = new Map<string, string>();
  for (const name of names) {
    const existingId = existingByName.get(name);
    if (existingId) {
      result.set(name, existingId);
      continue;
    }
    const created = await createFolder(parentFolderId, name, accessToken);
    result.set(name, created.folderId);
  }

  return result;
}
